import { useMemo } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { CheckBox } from '@bambi/component-library/src/components/inputs/CheckBox/CheckBox';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { SearchTypeahead } from '@bambi/component-library/src/components/SearchTypeahead/SearchTypeahead';
import debounce from 'lodash-es/debounce';


// Generic interface for items with id field
interface ItemWithId {
  id: string | number;
  [key: string]: any;
}

// Generic component for filtered item selection
interface ItemSelectorProps<T extends ItemWithId> {
  items: T[];
  selectedItems: T[];
  loading?: boolean;
  searching?: boolean;
  onSelectionChange: (items: T[]) => void;
  onSearchChange?: (searchTerm: string) => void;
  searchPlaceholder?: string;
  renderItem?: (item: T) => React.ReactNode;
  getItemLabel?: (item: T) => string;
  dataTestId?: string;
  noItemsText?: string;
}

export function ItemSelector<T extends ItemWithId>({
  items,
  selectedItems,
  loading,
  searching = false,
  onSelectionChange,
  onSearchChange,
  searchPlaceholder = 'Search items',
  renderItem,
  getItemLabel,
  dataTestId = 'filtered-item-search',
  noItemsText = 'No items found.',
}: ItemSelectorProps<T>) {
  const debouncedSetValue = useMemo(
    () =>
      onSearchChange
        ? debounce((value: string) => {
            onSearchChange(value);
          }, 300)
        : () => {},
    [onSearchChange]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {onSearchChange && (
        <SearchTypeahead
          dataTestId={dataTestId}
          onChange={debouncedSetValue}
          placeholder={searchPlaceholder}
          adornment={searching ? <LoadingIndicator variant="small" /> : <></>}
        />
      )}
      <div className="flex justify-between gap-2">
        <Button
          variant="ghost"
          onClick={() => {
            onSelectionChange(items);
          }}
          className="!p-1 !text-xs"
          disabled={items.length === 0 || selectedItems.length === items.length}
        >
          Select all ({items.length})
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            onSelectionChange([]);
          }}
          className="!p-1 !text-xs"
          disabled={selectedItems.length === 0}
        >
          Clear
        </Button>
      </div>
      <div className="mt-4 flex max-h-40 flex-col gap-2 overflow-y-auto">
        {items.length === 0 && (
          <p className="text-sm text-gray-500">{noItemsText}</p>
        )}
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-2">
            <CheckBox
              id={item.id.toString()}
              value={item.id.toString()}
              onCheckedChange={() => {
                onSelectionChange(
                  selectedItems.some((i) => i.id === item.id)
                    ? selectedItems.filter((i) => i.id !== item.id)
                    : [...selectedItems, item]
                );
              }}
              checked={!!selectedItems.find((i) => i.id === item.id)}
            />
            <label
              className="text-sm"
              data-testid={`item-${item.id}`}
              htmlFor={item.id.toString()}
            >
              {renderItem
                ? renderItem(item)
                : getItemLabel
                ? getItemLabel(item)
                : item.id}
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
